import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, Tooltip, LabelList, ResponsiveContainer, Cell } from 'recharts';
import {
  chartColorsFor,
  chartMutedTextColor,
  chartNeutralColor,
  chartSubtitleColor,
  chartTitleColor,
} from './chartColors';

interface ChartItem {
  companyTime: string;
  count: string;
}

const NOT_INFORMED = 'Não informado';

export const CompanyTimeMedicalCertificate = ({ data }: { data: ChartItem[] }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';

  const barColors = chartColorsFor(isDark);
  const notInformedColor = chartNeutralColor(isDark);
  const mutedText = chartMutedTextColor(isDark);

  const chartData = data
    .filter(item => item.companyTime !== NOT_INFORMED || Number(item.count) > 0)
    .map((item, index) => ({
      companyTime: item.companyTime,
      documentos: Number(item.count),
      color: item.companyTime === NOT_INFORMED ? notInformedColor : barColors[index % barColors.length],
    }));

  const maxCount = Math.max(...chartData.map(item => item.documentos), 0);
  const controllerHeight = Math.max(chartData.length * 38 + 40, 140);

  return (
    <div style={{ width: '100%' }}>
      <p style={{ color: chartTitleColor, fontSize: 20, fontWeight: 700, margin: '0 0 4px 0', padding: '1px' }}>
        Quantidade de documentos por tempo de empresa
      </p>
      <span style={{ color: chartSubtitleColor, fontSize: 13, fontWeight: 400 }}>
        Documentos por faixa de tempo de empresa dos colaboradores
      </span>

      <ResponsiveContainer width="100%" height={controllerHeight}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 16, right: 50, bottom: 10, left: 10 }}>
          <XAxis
            type="number"
            domain={[0, maxCount + 1]}
            allowDecimals={false}
            tick={{ fontSize: 12, fill: mutedText }}
            axisLine={{ stroke: palette.divider }}
            tickLine={false}
          />

          <YAxis
            type="category"
            dataKey="companyTime"
            width={120}
            tick={{ fontSize: 13, fill: palette.text.primary, fontWeight: 600 }}
            tickLine={false}
            axisLine={false}
          />

          <Tooltip
            cursor={{ fill: palette.action.hover }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0].payload;
              return (
                <div
                  style={{
                    background: palette.background.paper,
                    border: `1px solid ${palette.divider}`,
                    borderRadius: 8,
                    padding: '10px 14px',
                    fontSize: 13,
                    color: palette.text.primary,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    maxWidth: 260,
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{item.companyTime}</p>
                  <p style={{ margin: '6px 0 0', color: mutedText }}>
                    Documentos: <strong style={{ color: palette.text.primary }}>{item.documentos}</strong>
                  </p>
                  {item.companyTime === NOT_INFORMED ? (
                    <p style={{ margin: '4px 0 0', color: mutedText }}>
                      Documentos de colaboradores sem data de admissão ou sem vínculo válido.
                    </p>
                  ) : null}
                </div>
              );
            }}
          />

          <Bar dataKey="documentos" name="Documentos" radius={[0, 6, 6, 0]} barSize={22}>
            {chartData.map(item => (
              <Cell key={item.companyTime} fill={item.color} />
            ))}
            <LabelList
              dataKey="documentos"
              position="right"
              style={{ fontSize: 12, fill: palette.text.primary, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
